import type ISubscriptionRepository from "../ISubscriptionRepository";
import type {
  Filters,
  StatusRq,
  Subscription,
  SubscriptionCreateRq,
  SubscriptionDailyUsageFilter,
  SubscriptionList,
  SubscriptionMonthlyUsageFilter,
  SubscriptionUserCreateRq,
  SubscriptionUserFilter,
  SubscriptionUserStatDTO,
  SubscriptionUserUpdateRq,
  SubscriptionUsersActivityStats,
  ExternalSubscriptionCreateRq,
  SubscriptionUserDTO,
  RemoteSubscrptionUserCreateDTO,
  SubscriptionMonthlyUserFilter,
  MonthlySubscriptionUserDTO,
  UserSubscriptionUpdateDTO,
  SubscriptionMemoUpdateDTO,
} from "../subscription.types";
import type { AxiosRequestConfig } from "axios";
import { Buffer } from "buffer";
import { injectable } from "inversify";
import qs from "qs";
import API from "@repositories/Axios";
import type { FileSimpleDTO } from "@repositories/fileRepository/file.types";
import type { DailyUsageDTO, MonthlyUsageDTO } from "@repositories/usage";
import type { UsageWorkflowDTO } from "@repositories/workflowRepository/workflow.types";
import type { Page } from "@type/Page";

@injectable()
export class SubscriptionRepository implements ISubscriptionRepository {
  async addRemoteUser(
    subscriptionId: string,
    rqData: RemoteSubscrptionUserCreateDTO
  ): Promise<SubscriptionUserDTO> {
    const result = await API.post<SubscriptionUserDTO>(
      `/api/v1/subscriptions/${subscriptionId}/remote-users`,
      rqData
    );
    return result.data;
  }

  async getMySubscriptions(): Promise<Page<SubscriptionList>> {
    const result = await API.get<Page<SubscriptionList>>(
      `/api/v1/my-subscriptions`
    );
    return result.data;
  }

  async create(data: SubscriptionCreateRq): Promise<Subscription> {
    const result = await API.post<Subscription>("/api/v1/subscriptions", data);
    return result.data;
  }

  async getOne(id: string): Promise<Subscription> {
    const result = await API.get<Subscription>(`/api/v1/subscriptions/${id}`);
    return result.data;
  }
  async getList(queries?: Filters): Promise<Page<SubscriptionList>> {
    const queryString = qs.stringify(queries, {
      arrayFormat: "repeat",
      skipNulls: true,
    });
    const result = await API.get<Page<SubscriptionList>>(
      `/api/v1/subscriptions?${queryString}`
    );
    return result.data;
  }
  async update(id: string, data: SubscriptionCreateRq): Promise<Subscription> {
    const result = await API.put<Subscription>(
      `/api/v1/subscriptions/${id}`,
      data
    );
    return result.data;
  }
  async delete(id: string): Promise<void> {
    await API.delete<void>(`/api/v1/subscriptions/${id}`);
  }
  async createExternal(
    data: ExternalSubscriptionCreateRq,
    files?: File[]
  ): Promise<Subscription> {
    const formData = new FormData();
    formData.append(
      "subscription",
      new Blob([JSON.stringify(data)], { type: "application/json" })
    );
    if (files) {
      files.forEach((file) => formData.append("files", file));
    }
    const result = await API.post<Subscription>(
      "/api/v1/external-subscriptions",
      formData,
      {
        headers: {
          "Content-Type": undefined, // FormData 사용 시 Content-Type을 브라우저가 자동 설정하도록 함
        },
      }
    );
    return result.data;
  }
  async updateStatus(id: string, data: StatusRq): Promise<Subscription> {
    const result = await API.put<Subscription>(
      `/api/v1/subscriptions/${id}/status`,
      data
    );
    return result.data;
  }
  async addUser(
    subscriptionId: string,
    data: SubscriptionUserCreateRq
  ): Promise<SubscriptionUserDTO> {
    const result = await API.post<SubscriptionUserDTO>(
      `/api/v1/subscriptions/${subscriptionId}/users`,
      data
    );
    return result.data;
  }

  async getUserList(
    subscriptionId: string,
    filter: SubscriptionUserFilter
  ): Promise<Page<SubscriptionUserDTO>> {
    const queryString = qs.stringify(filter, { skipNulls: true });
    const result = await API.get<Page<SubscriptionUserDTO>>(
      `/api/v1/subscriptions/${subscriptionId}/users?${queryString}`
    );
    return result.data;
  }

  async getSubscriptionUser(
    subscriptionId: string,
    userLoginId: string
  ): Promise<SubscriptionUserDTO> {
    // NOTE. email 정보는 보안 이슈 때문에 직접 URL 에 사용할 수 없어 인코딩해서 전달
    const encodedLoginId = Buffer.from(userLoginId, "utf-8").toString("base64");
    const result = await API.get<SubscriptionUserDTO>(
      `/api/v1/subscriptions/${subscriptionId}/users/${encodedLoginId}`
    );
    return result.data;
  }

  async getUserStatus(
    subscriptionId: string
  ): Promise<SubscriptionUserStatDTO> {
    const queryString = qs.stringify({ subscriptionId });
    const result = await API.get<SubscriptionUserStatDTO>(
      `/api/v1/statistics/subscription-user-status?${queryString}`
    );
    return result.data;
  }

  async updateUser(
    subscriptionId: string,
    userLoginId: string,
    data: SubscriptionUserUpdateRq
  ): Promise<SubscriptionUserDTO> {
    const encodedLoginId = Buffer.from(userLoginId, "utf-8").toString("base64");
    const result = await API.put<SubscriptionUserDTO>(
      `/api/v1/subscriptions/${subscriptionId}/users/${encodedLoginId}`,
      data
    );
    return result.data;
  }

  async deleteUser(
    subscriptionId: string,
    userLoginIdOrUserId: string
  ): Promise<void> {
    const encodedLoginId = Buffer.from(userLoginIdOrUserId, "utf-8").toString(
      "base64"
    );
    await API.delete(
      `/api/v1/subscriptions/${subscriptionId}/users/${encodedLoginId}`
    );
  }

  async getDailyUsage(
    subscriptionId: string,
    queries: SubscriptionDailyUsageFilter
  ): Promise<Page<DailyUsageDTO>> {
    const queryString = qs.stringify(queries);
    const result = await API.get<Page<DailyUsageDTO>>(
      `/api/v1/subscriptions/${subscriptionId}/daily-usages?${queryString}`
    );
    return result.data;
  }

  async getMonthlyUsage(
    subscriptionId: string,
    queries: SubscriptionMonthlyUsageFilter
  ): Promise<Page<MonthlyUsageDTO>> {
    const queryString = qs.stringify(queries);
    const result = await API.get<Page<MonthlyUsageDTO>>(
      `/api/v1/subscriptions/${subscriptionId}/monthly-usages?${queryString}`
    );
    return result.data;
  }

  async getMonthlyUser(
    subscriptionId: string,
    filter: SubscriptionMonthlyUserFilter
  ): Promise<Page<MonthlySubscriptionUserDTO>> {
    const queryString = qs.stringify(filter);
    const result = await API.get<Page<MonthlySubscriptionUserDTO>>(
      `/api/v1/subscriptions/${subscriptionId}/monthly-users?${queryString}`
    );
    return result.data;
  }

  async getUsersActivityStats(
    subscriptionId: string
  ): Promise<SubscriptionUsersActivityStats> {
    const result = await API.get<SubscriptionUsersActivityStats>(
      `/api/v1/actions/subscription-users-activity-stats/${subscriptionId}`
    );
    return result.data;
  }

  async updateUserSubscription(
    id: string,
    data: UserSubscriptionUpdateDTO
  ): Promise<Subscription> {
    const result = await API.put<Subscription>(
      `/api/v1/manager/subscriptions/${id}`,
      data
    );
    return result.data;
  }

  async updateSubscriptionMemo(
    subscriptionId: string,
    data: SubscriptionMemoUpdateDTO,
    config?: AxiosRequestConfig
  ): Promise<SubscriptionMemoUpdateDTO> {
    const result = await API.patch<SubscriptionMemoUpdateDTO>(
      `/api/v1/subscriptions/${subscriptionId}/memo`,
      data,
      config
    );
    return result.data;
  }

  async fileUpload(
    { subscriptionId, files }: { subscriptionId: string; files: Blob[] },
    config?: AxiosRequestConfig
  ): Promise<FileSimpleDTO[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    const result = await API.post<FileSimpleDTO[]>(
      `/api/v1/subscriptions/${subscriptionId}/files`,
      formData,
      {
        ...config,
        headers: {
          ...config?.headers,
          "Content-Type": undefined, // FormData 사용 시 Content-Type을 브라우저가 자동 설정하도록 함
        },
      }
    );
    return result.data;
  }

  async fileDelete(subscriptionId: string, fileId: string): Promise<void> {
    await API.delete(`/api/v1/subscriptions/${subscriptionId}/files/${fileId}`);
  }

  async activateUsageWorkflow(subscriptionId: string): Promise<UsageWorkflowDTO> {
    const result = await API.post<UsageWorkflowDTO>(
      `/api/v1/subscriptions/${subscriptionId}/activate-usage-workflow`
    );
    return result.data;
  }

  async deactivateUsageWorkflow(subscriptionId: string): Promise<UsageWorkflowDTO> {
    const result = await API.post<UsageWorkflowDTO>(
      `/api/v1/subscriptions/${subscriptionId}/deactivate-usage-workflow`
    );
    return result.data;
  }

  async executeUsageWorkflow(subscriptionId: string): Promise<UsageWorkflowDTO> {
    const result = await API.post<UsageWorkflowDTO>(
      `/api/v1/subscriptions/${subscriptionId}/execute-usage-workflow`
    );
    return result.data;
  }
}
